import * as React from 'react';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { HostedStackedElementContainer, DialogWidget, DialogType, DialogPositioningBehavior, ButtonType, DialogFooterButtonsWidget, InlineAlertWidget, InlineAlertType } from '@tableau/widgets';
import { Formatters, Messages } from '../../codegen/Localize';
import { RefreshTaskType } from '../../ts/ServerApi/requests/CreateSelfServiceScheduleExtractTasksRequest';
import { ExtractTaskType } from '../../ts/ServerApi/types/IExtractTask';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { L10n } from '../../ts/L10n/L10n';

export interface RefreshRunNowDialogProps {
    description: string;
    title: string;
    close: (result?: RefreshTaskType) => void;
    disableIncremental?: boolean;
}

export class RefreshRunNowDialog extends React.Component<RefreshRunNowDialogProps> {
    public static async open(tasks: IContentInfo[], disableIncremental?: boolean): Promise<RefreshTaskType | undefined> {
        return new Promise((resolve, reject) => {
          const hostContainer = new HostedStackedElementContainer();
          const close = (type?: RefreshTaskType) => {
            hostContainer && hostContainer.remove();
            resolve(type);
          };
          const title = RefreshRunNowDialog.getTitle(tasks);
          const description = RefreshRunNowDialog.getDescription(tasks);
          const props: RefreshRunNowDialogProps = { disableIncremental, close, title, description };
          const dialog = React.createElement(RefreshRunNowDialog, props);
          hostContainer.render(dialog);
        });
    }

    public static getTitle(items: IContentInfo[]): string {
        if (items.length > 1) {
            return Messages.refreshRunNowTitlePlural();
        } else {
            return Messages.refreshRunNowTitle();
        }
    }

    public static getDescription(items: IContentInfo[]): string {
        if (items.length === 1) {
            let item = items[0];
            if (item.contentType === 'datasource') {
                return Messages.extractTaskForDataSourceRunPrompt({DATASOURCE_NAME: L10n.elideIfLong(item.name)});
            }
            if (item.contentType === 'workbook') {
                return Messages.extractTaskForWorkbookRunPrompt({WORKBOOK_NAME: L10n.elideIfLong(item.name)});
            }
        }
        return Messages.extractTasksRunPrompt({ITEM_COUNT_LOC: Formatters.formatCount(items.length)});
    }

    public renderFooter(): React.ReactNode {
    return <DialogFooterButtonsWidget
        buttons={[
        {
            handleClick: () => this.props.close(),
            buttonType: ButtonType.Outline,
            children: Messages.cancel(),
            testId: 'footer-cancel'
        },
        {
            handleClick: () => this.props.close('IncrementExtract'),
            buttonType: ButtonType.Outline,
            disabled: this.props.disableIncremental,
            children: Messages.refreshRunNowConfirmIncrementalButton(),
            testId: 'footer-confirm-incremental',
        },
        {
            handleClick: () => this.props.close('RefreshExtract'),
            buttonType: ButtonType.Outline,
            children: Messages.refreshRunNowConfirmFullButton(),
            testId: 'footer-confirm-full',
        },
        ]}
        testId="run-now-dialog-footer"
    />;
    }

    public render(): React.ReactNode {
        return <DialogWidget
            testId="run-now-inner"
            type={DialogType.NormalWithTitleBar}
            title={this.props.title}
            onCloseRequest={this.props.close}
            showCloseButton={true}
            positioningBehavior={DialogPositioningBehavior.Centered}
            isModal={true}
            showFooter={true}
            handleGlassClick={this.props.close}
            isGlassTransparent={false}
            footerContent={this.renderFooter()}
            dialogWidth={600}
            dialogBodyStyle={this.props.disableIncremental && {
                marginBottom: -16, // Compensate for extra padding the alert widget adds
            }}
        >
            {this.props.description}
            {this.props.disableIncremental && <InlineAlertWidget
                testId='increment-unavailable-alert'
                type={InlineAlertType.Info}
                children={Messages.refreshSelfServiceScheduleIncrementExtractsUnavailable()}
                style={{
                    marginTop: 10,
                }}
            />}
        </DialogWidget>;
    }
}